import * as React from "react";
import * as Pages from "@/components/Pages";
import csrftoken from "@/utils/csrf_token";

interface Result {
  id: string;
  source_id: string;
  type: string;
  title: string;
  snippet: string;
  body: string;
  team: string | null;
  date: string | null;
}

function Page() {
  // The page lives at /<companyId>/wiki; the search endpoint is /<companyId>/api/wiki/search.
  const companyId = window.location.pathname.split("/")[1];

  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState<Result[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [searched, setSearched] = React.useState(false);
  const [openId, setOpenId] = React.useState<string | null>(null);
  const [team, setTeam] = React.useState<string | null>(null);

  // Inline edit state. Only knowledge entries are editable; an edit here flows back to the
  // agent's own memory file on the next sync (Wiki wins, but never deletes what the agent added).
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editTitle, setEditTitle] = React.useState("");
  const [editBody, setEditBody] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  const runSearch = async (q: string) => {
    if (!q.trim()) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`/${companyId}/api/wiki/search?q=${encodeURIComponent(q)}`, {
        headers: { Accept: "application/json" },
      });
      const data = await res.json();
      setResults(data.results || []);
    } catch {
      setResults([]);
    }
    setOpenId(null);
    setTeam(null);
    setEditingId(null);
    setLoading(false);
    setSearched(true);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    runSearch(query);
  };

  const startEdit = (e: React.MouseEvent, r: Result) => {
    e.stopPropagation();
    setEditingId(r.id);
    setEditTitle(r.title || "");
    setEditBody(r.body || "");
  };

  const cancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(null);
  };

  const saveEdit = async (e: React.MouseEvent, r: Result) => {
    e.stopPropagation();
    setSaving(true);
    try {
      const res = await fetch(`/${companyId}/api/wiki/knowledge/${r.source_id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-csrf-token": csrftoken() || "",
        },
        body: JSON.stringify({ title: editTitle, body: editBody }),
      });
      if (res.ok) {
        setResults((rs) =>
          rs.map((x) => (x.id === r.id ? { ...x, title: editTitle, body: editBody, snippet: editBody.slice(0, 240) } : x)),
        );
        setEditingId(null);
      }
    } catch {
      // leave the editor open so the human can retry
    }
    setSaving(false);
  };

  const teams = Array.from(new Set(results.map((r) => r.team).filter(Boolean))) as string[];
  const shown = team ? results.filter((r) => r.team === team) : results;

  return (
    <Pages.Page title="Wiki">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-content-accent mb-1">Company Wiki</h1>
        <div className="text-sm text-content-dimmed mb-5">
          Search across everything the company knows: team notes, tasks, discussions, documents, contacts, goals, and
          projects. Results show who noted them and when, weigh them as leads, not gospel. Edit a team note here and the
          correction syncs back to that team's own memory.
        </div>

        <form onSubmit={onSubmit} className="mb-6">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the whole company..."
            data-test-id="wiki-search"
            className="w-full text-base border border-surface-outline rounded-lg px-4 py-3 bg-surface-base focus:outline-none focus:ring-2 focus:ring-brand-1"
          />
        </form>

        <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-6 items-start">
          <aside className="lg:sticky lg:top-4 border border-stroke-base rounded-2xl p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-content-dimmed mb-2">Teams</div>
            <button
              onClick={() => setTeam(null)}
              className={"block w-full text-left text-sm px-2 py-1 rounded " + (team === null ? "bg-surface-highlight font-medium" : "")}
            >
              All{searched ? ` (${results.length})` : ""}
            </button>
            {teams.map((t) => (
              <button
                key={t}
                onClick={() => setTeam(t)}
                className={"block w-full text-left text-sm px-2 py-1 rounded " + (team === t ? "bg-surface-highlight font-medium" : "")}
              >
                {t} ({results.filter((r) => r.team === t).length})
              </button>
            ))}
          </aside>

          <main>
            {loading && <div className="text-sm text-content-dimmed">Searching...</div>}
            {!loading && !searched && <div className="text-sm text-content-dimmed">Type a question or some keywords above.</div>}
            {!loading && searched && shown.length === 0 && <div className="text-sm text-content-dimmed">No results.</div>}

            <div className="flex flex-col gap-2">
              {shown.map((r) => (
                <div
                  key={r.id}
                  onClick={() => setOpenId(openId === r.id ? null : r.id)}
                  className="border border-surface-outline rounded-xl p-3 hover:bg-surface-highlight cursor-pointer"
                  data-test-id="wiki-result"
                >
                  <div className="flex items-center gap-2 text-xs text-content-dimmed mb-1">
                    <span className="inline-flex items-center rounded-full border border-surface-outline px-2 py-0.5">{r.type}</span>
                    <span>{r.team || "?"}</span>
                    <span>· {(r.date || "").slice(0, 10)}</span>
                    {openId === r.id && r.type === "knowledge" && editingId !== r.id && (
                      <button
                        onClick={(e) => startEdit(e, r)}
                        data-test-id="wiki-edit"
                        className="ml-auto text-brand-1 hover:underline"
                      >
                        Edit
                      </button>
                    )}
                  </div>

                  {editingId === r.id ? (
                    <div onClick={(e) => e.stopPropagation()} className="cursor-default">
                      <input
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        className="w-full text-sm font-medium border border-surface-outline rounded px-2 py-1 mb-2 bg-surface-base"
                      />
                      <textarea
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                        rows={Math.min(20, Math.max(6, editBody.split("\n").length + 1))}
                        data-test-id="wiki-edit-body"
                        className="w-full text-sm border border-surface-outline rounded px-2 py-2 bg-surface-base font-mono"
                      />
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={(e) => saveEdit(e, r)}
                          disabled={saving}
                          data-test-id="wiki-save"
                          className="text-sm px-3 py-1 rounded bg-brand-1 text-white-1 disabled:opacity-50"
                        >
                          {saving ? "Saving..." : "Save"}
                        </button>
                        <button onClick={cancelEdit} className="text-sm px-3 py-1 rounded border border-surface-outline">
                          Cancel
                        </button>
                        <span className="text-xs text-content-dimmed">Syncs back to the team's memory. Wiki wins; nothing the team added is dropped.</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="font-medium text-content-accent">{r.title || "(untitled)"}</div>
                      <div className="text-sm text-content-base mt-1 whitespace-pre-wrap">{openId === r.id ? r.body || r.snippet : r.snippet}</div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </main>
        </div>
      </div>
    </Pages.Page>
  );
}

export default { name: "WikiPage", loader: async () => ({}), Page };
